import React, {useState, useEffect} from "react"

import magicLoginService from "@irati/auth/services/magicLogin"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import FormattedMessage, {getMessage, useIntl} from "@irati/utils/lang"
import {CreateConfirmIdentifierResponse} from "@irati/types"
import {VERIFY_PHONE_NO} from "@irati-web/platform/src/containers/OneTimePassword"

import {PhoneInput} from "@irati/ui/src/FormItem"
import Flex from "@irati/ui/src/Flex"
import Button, {IconTypes} from "@irati/ui/src/Button"
import usersService from "@irati/identity/services/users"
import authentication from "@irati/auth"
import Icon from "@irati/ui/src/Icon"
import tracking from "@irati-tracking/platform"
import {LoginProduct, LoginType} from "@irati-tracking/platform/platform"
import ConfirmPhoneCode from "./ConfirmPhoneCodeModal"
import EnterPhoneNoModal from "./EnterPhoneNoModal"
import ConfirmDeletePhoneNo from "./ConfirmDeletePhoneNoModal"
import VerifyBadge from "./VerifyBadge"

const VerifyPhoneNumber: React.FC = () => {
  const [showEnterPhoneNo, setEnterPhoneNo] = useState(false)
  const [showConfirmCode, setConfirmCode] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isPhoneNoVerified, setIsPhoneNoVerified] = useState(false)
  const [savedPhone, setSavedPhone] = useState("")
  const [phone, setPhone] = useState("")
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [errorMsg, setErrorMsg] = useState("")
  const [confirmPhoneLogin, setConfirmPhoneLogin] = useState<CreateConfirmIdentifierResponse>()
  const {formatMessage} = useIntl()

  const handleOnHide = (value?: string) => {
    if (typeof value === "string") {
      setSavedPhone(value)
      setPhone(value)
    } else if (savedPhone) {
      setPhone(savedPhone)
    }
    setEnterPhoneNo(false)
    setConfirmCode(false)
  }

  const handleOnNext = (phoneNumber: string) => {
    setErrorMsg("")
    setPhone(phoneNumber)
    if (phoneNumber) {
      sendPhoneOTPCode(phoneNumber)
      tracking.loginOTPSubmit("phone_number", LoginType.phoneCapture, LoginProduct.myAccount, undefined, phoneNumber)
    }
  }

  const sendPhoneOTPCode = (phoneNumber: string) => {
    const loaded = magicLoginService
      .createConfirmIdentifier(phoneNumber)
      .then((res) => {
        setEnterPhoneNo(false)
        setConfirmPhoneLogin(res)
        setConfirmCode(true)
      })
      .catch((err) => {
        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        setErrorMsg(message)
      })
    setLoad(loaded)
  }

  const handleOnResend = () => {
    sendPhoneOTPCode(phone)
  }

  const handlePhoneNoChange = () => {
    setConfirmCode(false)
    setEnterPhoneNo(true)
  }

  const handleEditBtn = () => {
    tracking.loginCTAClick("edit_phone_number", LoginType.phoneCapture, LoginProduct.myAccount)
    setEnterPhoneNo(true)
  }

  const handleAddBtn = () => {
    tracking.loginCTAClick("add_phone_number", LoginType.phoneCapture, LoginProduct.myAccount)
    setEnterPhoneNo(true)
  }

  const handleDeleteBtn = () => {
    tracking.loginCTAClick("delete_phone_number", LoginType.phoneCapture, LoginProduct.myAccount)
    setShowDeleteModal(true)
  }

  const handleDelete = () => {
    tracking.loginCTAClick("comfirm_delete_phone_number", LoginType.phoneCapture, LoginProduct.myAccount)
    usersService.deleteUserPhoneNo(authentication.user().uuid).then(() => {
      localStorage.setItem(VERIFY_PHONE_NO, "{}")
      setPhone("")
      setSavedPhone("")
      setIsPhoneNoVerified(false)
      setShowDeleteModal(false)
    })
  }

  useEffect(() => {
    usersService.getUserVerifiedPhoneNo(authentication.user().uuid, {fetchPolicy: "network-only"}).then(({e164PhoneNo, intlPhoneNo}) => {
      if (e164PhoneNo) {
        setSavedPhone(e164PhoneNo)
        setPhone(e164PhoneNo)
        setIsPhoneNoVerified(true)
      } else if (intlPhoneNo) {
        setSavedPhone(intlPhoneNo)
        setPhone(intlPhoneNo)
      }
    })
  }, [])

  const editText = isPhoneNoVerified ? "account.profile.verifyPhoneNo.button.edit" : "account.profile.verifyPhoneNo.button.verify"

  return (
    <>
      {savedPhone && <VerifyBadge isVerified={isPhoneNoVerified} />}
      <PhoneInput
        value={savedPhone}
        label={formatMessage({id: "account.profile.verifyPhoneNo.inputLabel"})}
        placeholder={formatMessage({id: "account.profile.verifyPhoneNo.inputPlaceholder"})}
        disabled
      />
      <Flex justifyContent={savedPhone ? "space-between" : "flex-end"} marginBottom={2}>
        {savedPhone ? (
          <>
            <Button tertiary onClick={handleEditBtn}>
              <Flex gap={1} alignItems="center">
                <FormattedMessage id={editText} />
                <Icon icon={IconTypes.edit} size={12} />
              </Flex>
            </Button>
            <Button tertiary onClick={handleDeleteBtn}>
              <Flex gap={1} alignItems="center">
                <FormattedMessage id="account.profile.verifyPhoneNo.button.delete" />
                <Icon marginLeft icon={IconTypes.trashcan} size={12} />
              </Flex>
            </Button>
          </>
        ) : (
          <Button tertiary onClick={handleAddBtn}>
            <Flex gap={1} alignItems="center">
              <FormattedMessage id="account.profile.verifyPhoneNo.button.add" />
              <Icon icon={IconTypes.plus} size={12} />
            </Flex>
          </Button>
        )}
      </Flex>
      <EnterPhoneNoModal
        phoneNo={phone}
        show={showEnterPhoneNo}
        onHide={handleOnHide}
        onNext={handleOnNext}
        load={load}
        error={errorMsg}
      />
      <ConfirmPhoneCode
        show={showConfirmCode}
        phone={phone}
        login={confirmPhoneLogin}
        onHide={handleOnHide}
        onResend={handleOnResend}
        onPhoneNoChange={handlePhoneNoChange}
        setIsPhoneNoVerified={setIsPhoneNoVerified}
      />
      <ConfirmDeletePhoneNo show={showDeleteModal} onHide={() => setShowDeleteModal(false)} onDelete={handleDelete} />
    </>
  )
}

export default VerifyPhoneNumber
